import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
    DollarSign, Plus, Settings, Pencil, Trash2, Save, X,
    Loader2, ChevronDown, ChevronRight, Check,
} from 'lucide-react';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';
import {
    DataErrorPanel,
    StaleDataNotice,
    fetchJson,
    useRemoteData,
} from './WeddingLoadState';
import { useDialogA11y } from './useDialog';

// ─── constants ───────────────────────────────────────────────────────────────

const CURRENCIES = ['SGD', 'CAD', 'USD', 'EUR', 'GBP', 'MXN'];

// `text-base` (16px) on phones, `sm:text-sm` above. Under 16px, iOS Safari
// zooms the whole page the moment a field takes focus — on a budget sheet
// that means the Save button walks off-screen mid-edit.
const INPUT_CLS =
    'rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-base sm:text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent transition';

// A 44x44 hit area for a small icon, with the extra width pulled back into
// the surrounding padding so the compact layout is unchanged.
const ICON_BUTTON_CLS =
    'flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition focus:outline-none focus-visible:ring-2';

const SETTINGS_TITLE_ID = 'budget-settings-dialog-title';
const ITEM_TITLE_ID = 'budget-item-dialog-title';
const PAID_LABEL_ID = 'budget-item-paid-label';

const EMPTY_ITEM_FORM = {
    category: '',
    vendor: '',
    description: '',
    estimated_cost: '',
    actual_cost: '',
    currency: 'SGD',
    is_paid: false,
    notes: '',
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmt(amount, currency = 'SGD') {
    // null/undefined means "we could not compute this" (e.g. a missing FX
    // rate). Rendering it as 0.00 would be a misleading total.
    if (amount == null) return `${currency} —`;
    if (amount === '' || isNaN(Number(amount))) return `${currency} 0.00`;
    return `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function progressColor(percent) {
    if (percent > 95) return 'bg-red-500';
    if (percent > 80) return 'bg-amber-400';
    return 'bg-emerald-500';
}

// ─── settings modal ──────────────────────────────────────────────────────────

function SettingsModal({ settings, onClose, onSaved, backgroundRef }) {
    const [form, setForm] = useState({
        total_budget: settings?.total_budget ?? '',
        currency: settings?.currency ?? 'SGD',
    });
    const [saving, setSaving] = useState(false);
    const containerRef = useRef(null);

    const handleDialogKeyDown = useDialogA11y({
        open: true, onClose, dialogRef: containerRef, backgroundRef,
    });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.total_budget || isNaN(Number(form.total_budget))) {
            toast.error('Enter a valid budget amount');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(ENDPOINTS.WEDDING_BUDGET_SETTINGS, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ total_budget: Number(form.total_budget), currency: form.currency }),
            });
            if (handleAuthError(res)) return;
            if (!res.ok) throw new Error('Save failed');
            const data = await res.json();
            toast.success('Budget settings updated!');
            onSaved(data);
        } catch {
            toast.error('Could not save settings.');
        } finally {
            setSaving(false);
        }
    };

    const inputCls = INPUT_CLS;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
            onClick={onClose}
        >
            <div
                ref={containerRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={SETTINGS_TITLE_ID}
                onKeyDown={handleDialogKeyDown}
                className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl outline-none"
                onClick={e => e.stopPropagation()}
            >
                <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 bg-white/95 backdrop-blur border-b border-stone-100 rounded-t-3xl">
                    <div className="flex items-center gap-2">
                        <Settings size={18} className="text-amber-700" aria-hidden="true" />
                        <h2
                            id={SETTINGS_TITLE_ID}
                            data-dialog-heading
                            tabIndex={-1}
                            className="text-base font-bold text-stone-800 outline-none"
                        >
                            Budget Settings
                        </h2>
                    </div>
                    {/* This control used to be an unnamed button wrapping a
                        decorative <X>: a screen reader announced "button",
                        full stop, and voice control had nothing to say. */}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close budget settings"
                        className={clsx(ICON_BUTTON_CLS, '-mr-2 rounded-full hover:bg-stone-100 focus-visible:ring-amber-400')}
                    >
                        <X size={18} className="text-stone-500" aria-hidden="true" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                    <div className="flex flex-col gap-1">
                        <label htmlFor="budget-settings-total" className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Total Budget</label>
                        <input
                            id="budget-settings-total"
                            type="number"
                            min="0"
                            step="any"
                            required
                            value={form.total_budget}
                            onChange={e => set('total_budget', e.target.value)}
                            className={inputCls}
                            placeholder="e.g. 50000"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label htmlFor="budget-settings-currency" className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Primary Currency</label>
                        <select
                            id="budget-settings-currency"
                            value={form.currency}
                            onChange={e => set('currency', e.target.value)}
                            className={inputCls}
                        >
                            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                    </div>

                    <div className="flex gap-3 pt-1 pb-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 min-h-[44px] rounded-2xl border border-stone-200 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 min-h-[44px] rounded-2xl bg-amber-700 py-2.5 text-sm font-bold text-white hover:bg-amber-800 transition disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                            {saving ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Save size={15} aria-hidden="true" />}
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─── add / edit item modal ───────────────────────────────────────────────────

function ItemModal({ item, categories, primaryCurrency, onClose, onSaved, backgroundRef }) {
    const isEdit = !!item?.id;
    const [form, setForm] = useState(item ? {
        category: item.category ?? '',
        vendor: item.vendor ?? '',
        description: item.description ?? '',
        estimated_cost: item.estimated_cost ?? '',
        actual_cost: item.actual_cost ?? '',
        currency: item.currency ?? primaryCurrency ?? 'SGD',
        is_paid: item.is_paid ?? false,
        notes: item.notes ?? '',
    } : { ...EMPTY_ITEM_FORM, currency: primaryCurrency ?? 'SGD' });
    const [saving, setSaving] = useState(false);
    const containerRef = useRef(null);

    const handleDialogKeyDown = useDialogA11y({
        open: true, onClose, dialogRef: containerRef, backgroundRef,
    });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.description.trim()) { toast.error('Description is required'); return; }
        if (!form.estimated_cost || isNaN(Number(form.estimated_cost))) { toast.error('Enter a valid estimated cost'); return; }
        setSaving(true);
        try {
            // A blank "Actual cost" is sent as an explicit `null`, never as 0
            // or ''. `null` is what tells the API to clear the column; 0 would
            // book a real payment of nothing, and '' is not a number at all.
            // The PUT reads the keys the client actually sent (exclude_unset),
            // so this null lands instead of being dropped as "not supplied".
            const payload = {
                ...form,
                estimated_cost: Number(form.estimated_cost),
                actual_cost: form.actual_cost !== '' ? Number(form.actual_cost) : null,
            };
            const url = isEdit ? `${ENDPOINTS.WEDDING_BUDGET_ITEMS}/${item.id}` : ENDPOINTS.WEDDING_BUDGET_ITEMS;
            const method = isEdit ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(payload) });
            if (handleAuthError(res)) return;
            if (!res.ok) throw new Error('Save failed');
            const data = await res.json();
            toast.success(isEdit ? 'Item updated!' : 'Item added!');
            onSaved(data.dashboard);
        } catch {
            toast.error('Could not save item. Try again.');
        } finally {
            setSaving(false);
        }
    };

    const inputCls = INPUT_CLS;

    // `htmlFor` is the whole point of this helper now: without it every one of
    // these inputs reached assistive tech unnamed, and the visible caption was
    // decoration sitting next to an anonymous text box.
    const field = (id, label, children) => (
        <div className="flex flex-col gap-1">
            <label htmlFor={id} className="text-xs font-semibold text-stone-500 uppercase tracking-wide">{label}</label>
            {children}
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
            onClick={onClose}
        >
            <div
                ref={containerRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={ITEM_TITLE_ID}
                onKeyDown={handleDialogKeyDown}
                className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92dvh] overflow-y-auto outline-none"
                onClick={e => e.stopPropagation()}
            >
                <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 bg-white/95 backdrop-blur border-b border-stone-100 rounded-t-3xl">
                    <div className="flex items-center gap-2">
                        <DollarSign size={18} className="text-rose-600" aria-hidden="true" />
                        <h2
                            id={ITEM_TITLE_ID}
                            data-dialog-heading
                            tabIndex={-1}
                            className="text-base font-bold text-stone-800 outline-none"
                        >
                            {isEdit ? 'Edit Item' : 'Add Budget Item'}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={isEdit ? 'Close edit item' : 'Close add budget item'}
                        className={clsx(ICON_BUTTON_CLS, '-mr-2 rounded-full hover:bg-stone-100 focus-visible:ring-rose-400')}
                    >
                        <X size={18} className="text-stone-500" aria-hidden="true" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Category - combo input */}
                    <div className="sm:col-span-2">
                        {field('budget-item-category', 'Category',
                            <input
                                id="budget-item-category"
                                list="category-options"
                                value={form.category}
                                onChange={e => set('category', e.target.value)}
                                className={inputCls}
                                placeholder="e.g. Venue, Catering, Flowers…"
                            />
                        )}
                        <datalist id="category-options">
                            {(categories || []).map(c => (
                                <option key={c.category} value={c.category} />
                            ))}
                        </datalist>
                    </div>

                    {field('budget-item-vendor', 'Vendor',
                        <input
                            id="budget-item-vendor"
                            value={form.vendor}
                            onChange={e => set('vendor', e.target.value)}
                            className={inputCls}
                            placeholder="Vendor name"
                        />
                    )}

                    {field('budget-item-description', 'Description *',
                        <input
                            id="budget-item-description"
                            required
                            value={form.description}
                            onChange={e => set('description', e.target.value)}
                            className={inputCls}
                            placeholder="What is this for?"
                        />
                    )}

                    {field('budget-item-estimated', 'Estimated Cost *',
                        <input
                            id="budget-item-estimated"
                            type="number"
                            min="0"
                            step="any"
                            required
                            value={form.estimated_cost}
                            onChange={e => set('estimated_cost', e.target.value)}
                            className={inputCls}
                            placeholder="0.00"
                        />
                    )}

                    {field('budget-item-actual', 'Actual Cost',
                        <input
                            id="budget-item-actual"
                            type="number"
                            min="0"
                            step="any"
                            value={form.actual_cost}
                            onChange={e => set('actual_cost', e.target.value)}
                            className={inputCls}
                            placeholder="0.00"
                        />
                    )}

                    {field('budget-item-currency', 'Currency',
                        <select
                            id="budget-item-currency"
                            value={form.currency}
                            onChange={e => set('currency', e.target.value)}
                            className={inputCls}
                        >
                            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                    )}

                    {/* is_paid toggle.
                        A <label> only labels a form control, never a <button>,
                        so this switch reached assistive tech as an unnamed
                        button whose state showed up as colour alone. It is now
                        a real switch: named by the caption beside it, state in
                        aria-checked, and a 44px-tall target. */}
                    <div className="sm:col-span-2 flex items-center gap-3 min-h-[44px]">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={form.is_paid}
                            aria-labelledby={PAID_LABEL_ID}
                            onClick={() => set('is_paid', !form.is_paid)}
                            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                        >
                            <span className={clsx(
                                'w-10 h-6 rounded-full transition-colors flex items-center',
                                form.is_paid ? 'bg-emerald-600' : 'bg-stone-200'
                            )}>
                                <span className={clsx(
                                    'w-5 h-5 rounded-full bg-white shadow ring-1 ring-inset ring-stone-500 transition-transform mx-0.5',
                                    form.is_paid ? 'translate-x-4' : 'translate-x-0'
                                )} />
                            </span>
                        </button>
                        <span id={PAID_LABEL_ID} className="text-sm font-medium text-stone-700">
                            {form.is_paid ? 'Paid' : 'Not paid yet'}
                        </span>
                    </div>

                    {/* Notes */}
                    <div className="sm:col-span-2">
                        {field('budget-item-notes', 'Notes',
                            <textarea
                                id="budget-item-notes"
                                value={form.notes}
                                onChange={e => set('notes', e.target.value)}
                                className={clsx(inputCls, 'resize-none h-20')}
                                placeholder="Any extra details…"
                            />
                        )}
                    </div>

                    {/* Actions */}
                    <div className="sm:col-span-2 flex gap-3 pt-1 pb-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 min-h-[44px] rounded-2xl border border-stone-200 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 min-h-[44px] rounded-2xl bg-rose-600 py-2.5 text-sm font-bold text-white hover:bg-rose-700 transition disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                            {saving ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Save size={15} aria-hidden="true" />}
                            {saving ? 'Saving…' : isEdit ? 'Update' : 'Add Item'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─── line item row ────────────────────────────────────────────────────────────

function LineItem({ item, primaryCurrency, onEdit, onDelete, onTogglePaid }) {
    return (
        <div className="flex items-start gap-3 px-4 py-3 border-b border-stone-100 last:border-0 group hover:bg-rose-50/20 transition">
            {/* Paid toggle.
                The visible tick stays a 20px circle; the button around it is
                44x44 and spends the difference on the row's own padding and
                gap, so the row looks identical and is four times easier to
                hit. `role="switch"` puts paid/unpaid in the accessibility
                tree instead of leaving it to a green fill. */}
            <button
                type="button"
                role="switch"
                aria-checked={Boolean(item.is_paid)}
                aria-label={`Paid: ${item.description}`}
                onClick={() => onTogglePaid(item)}
                className="-my-2 -ml-2 -mr-1 flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                title={item.is_paid ? 'Mark as unpaid' : 'Mark as paid'}
            >
                <span className={clsx(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center transition',
                    item.is_paid
                        ? 'border-emerald-600 bg-emerald-600 text-white'
                        : 'border-stone-500 hover:border-emerald-600'
                )}>
                    {item.is_paid && <Check size={11} aria-hidden="true" />}
                </span>
            </button>

            {/* Description & vendor */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-stone-800 truncate">{item.description}</span>
                    {item.currency && item.currency !== primaryCurrency && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            {item.currency}
                        </span>
                    )}
                    {item.is_paid && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            Paid
                        </span>
                    )}
                </div>
                {item.vendor && (
                    <div className="text-xs text-stone-500 mt-0.5 truncate">{item.vendor}</div>
                )}
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-xs text-stone-500">
                        Est: <span className="font-medium text-stone-700">{fmt(item.estimated_cost, item.currency || primaryCurrency)}</span>
                    </span>
                    {item.actual_cost != null && item.actual_cost !== '' && (
                        <span className="text-xs text-stone-500">
                            Act: <span className="font-medium text-stone-700">{fmt(item.actual_cost, item.currency || primaryCurrency)}</span>
                        </span>
                    )}
                </div>
            </div>

            {/* Actions.
                `group-focus-within` matters as much as the size here: on
                desktop these were revealed by hover only, so a keyboard user
                tabbed onto an invisible Delete. Icons stay 14px; the targets
                are 44x44 with the extra width taken out of the row padding. */}
            <div className="-my-2 -mr-2 flex items-center flex-shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition">
                <button
                    type="button"
                    onClick={() => onEdit(item)}
                    className={clsx(ICON_BUTTON_CLS, 'text-stone-500 hover:text-amber-700 hover:bg-amber-50 focus-visible:ring-amber-400')}
                    aria-label={`Edit ${item.description}`}
                    title="Edit"
                >
                    <Pencil size={14} aria-hidden="true" />
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(item.id)}
                    className={clsx(ICON_BUTTON_CLS, 'text-stone-500 hover:text-red-600 hover:bg-red-50 focus-visible:ring-red-400')}
                    aria-label={`Delete ${item.description}`}
                    title="Delete"
                >
                    <Trash2 size={14} aria-hidden="true" />
                </button>
            </div>
        </div>
    );
}

// ─── category card ────────────────────────────────────────────────────────────

function CategoryCard({ cat, panelId, budgetTarget, primaryCurrency, expanded, onToggle, onEdit, onDelete, onTogglePaid }) {
    const catConverted = cat.conversion_available !== false;
    const estimatedPercent = catConverted && budgetTarget > 0 && cat.estimated != null
        ? Math.min(100, (cat.estimated / budgetTarget) * 100)
        : 0;

    return (
        <div className="rounded-2xl border border-stone-200 bg-white/90 shadow-sm overflow-hidden">
            {/* Header.
                This is a disclosure, and until now the only thing that said so
                was which way the chevron pointed. `aria-expanded` reports the
                state, and `aria-controls` names the region it opens. */}
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                aria-controls={expanded ? panelId : undefined}
                className="w-full min-h-[44px] flex items-center gap-3 px-4 py-3.5 hover:bg-stone-50/60 transition text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
            >
                <span className="flex-shrink-0 text-stone-500">
                    {expanded ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
                </span>
                <span className="flex-1 min-w-0">
                    <span className="font-bold text-stone-800 text-sm">{cat.category}</span>
                    <span className="ml-2 text-xs text-stone-500">({cat.items?.length ?? 0} item{cat.items?.length !== 1 ? 's' : ''})</span>
                </span>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-stone-500 hidden sm:inline">
                        Est: <span className="font-semibold text-stone-700">{fmt(cat.estimated, primaryCurrency)}</span>
                    </span>
                    {!catConverted && (
                        <span
                            className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700"
                            title={`No exchange rate for ${(cat.unconverted_currencies ?? []).join(', ')}`}
                        >
                            FX unavailable
                        </span>
                    )}
                    {cat.actual > 0 && (
                        <span className="text-xs text-stone-500 hidden sm:inline">
                            Act: <span className="font-semibold text-stone-700">{fmt(cat.actual, primaryCurrency)}</span>
                        </span>
                    )}
                    {/* Mini progress bar */}
                    <div className="w-16 h-1.5 rounded-full bg-stone-100 overflow-hidden hidden sm:block">
                        <div
                            className={clsx('h-full rounded-full transition-all', progressColor(estimatedPercent))}
                            style={{ width: `${estimatedPercent}%` }}
                        />
                    </div>
                </div>
            </button>

            {/* Mobile amounts */}
            <div className="sm:hidden px-4 pb-2 flex gap-4">
                <span className="text-xs text-stone-500">
                    Est: <span className="font-semibold text-stone-700">{fmt(cat.estimated, primaryCurrency)}</span>
                </span>
                {cat.actual > 0 && (
                    <span className="text-xs text-stone-500">
                        Act: <span className="font-semibold text-stone-700">{fmt(cat.actual, primaryCurrency)}</span>
                    </span>
                )}
            </div>

            {/* Expanded items */}
            {expanded && (
                <div id={panelId} className="border-t border-stone-100">
                    {cat.items && cat.items.length > 0 ? (
                        cat.items.map(item => (
                            <LineItem
                                key={item.id}
                                item={item}
                                primaryCurrency={primaryCurrency}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onTogglePaid={onTogglePaid}
                            />
                        ))
                    ) : (
                        <div className="px-4 py-6 text-center text-xs text-stone-500 italic">
                            No items in this category yet.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── main component ───────────────────────────────────────────────────────────

/**
 * @param {object} [backgroundRef] Ref to the page content that sits behind
 *   this tab. While a budget dialog is open that subtree is marked `inert`,
 *   which is what `aria-modal="true"` promises. The dialogs are portalled to
 *   <body> so they are not inside the subtree they disable — and so the
 *   toast container, which lives outside the planner, keeps working.
 */
export default function WeddingBudget({ backgroundRef }) {
    const [showItemModal, setShowItemModal]       = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [editingItem, setEditingItem]           = useState(null);
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    const deletedItemRef = useRef(null);

    // ── fetch ────────────────────────────────────────────────────────────────
    // Three explicit states (never-loaded / loaded / loaded-then-refresh-failed)
    // so a failed request can never render the "No budget items yet" empty
    // state or invented totals. See WeddingLoadState.jsx.
    const loadDashboard = useCallback(
        () => fetchJson(ENDPOINTS.WEDDING_BUDGET, { label: 'budget' }),
        []
    );
    const resource = useRemoteData(loadDashboard);
    const dashboard = resource.data;
    const setDashboard = resource.setData;
    const fetchDashboard = resource.load;

    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    // ── derived values ───────────────────────────────────────────────────────
    // Only ever computed from a real response — the render below returns early
    // when `dashboard` is null, so none of these can be a placeholder.

    const totals          = dashboard?.totals ?? {};
    const primaryCurrency = dashboard?.primary_currency ?? dashboard?.settings?.currency ?? 'SGD';
    // No hardcoded fallback: an absent target means "not set yet", which is
    // rendered as such. The old `?? 50000` invented a plausible SGD 50,000
    // target whenever the fetch failed.
    const rawTarget       = totals.budget_target ?? dashboard?.settings?.total_budget ?? null;
    const budgetTarget    = rawTarget == null || isNaN(Number(rawTarget)) ? null : Number(rawTarget);
    const hasTarget       = budgetTarget != null && budgetTarget > 0;
    // A null total means the backend could not convert every item into the
    // primary currency. Keep it null so the UI shows an unavailable state
    // rather than a number that silently omits the unconverted amounts.
    const conversionAvailable = totals.conversion_available !== false;
    const estimated       = conversionAvailable ? totals.estimated ?? 0 : null;
    const actual          = conversionAvailable ? totals.actual ?? 0 : null;
    const paid            = conversionAvailable ? totals.paid ?? 0 : null;
    const percentEst      = !conversionAvailable
        ? null
        : totals.percent_estimated ?? (hasTarget ? ((estimated ?? 0) / budgetTarget) * 100 : null);
    const categories      = dashboard?.categories ?? [];

    // ── category toggle ──────────────────────────────────────────────────────

    const toggleCategory = (name) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    // ── item actions ─────────────────────────────────────────────────────────

    const openAdd = () => { setEditingItem(null); setShowItemModal(true); };
    const openEdit = (item) => { setEditingItem(item); setShowItemModal(true); };
    const closeItemModal = () => { setShowItemModal(false); setEditingItem(null); };

    const handleItemSaved = (updatedDashboard) => {
        if (updatedDashboard) setDashboard(updatedDashboard);
        closeItemModal();
    };

    const handleDelete = async (id) => {
        // Find the item in the current dashboard to store for undo
        const allItems = (dashboard?.categories ?? []).flatMap(c => c.items ?? []);
        const itemToDelete = allItems.find(i => i.id === id);

        try {
            const res = await fetch(`${ENDPOINTS.WEDDING_BUDGET_ITEMS}/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (handleAuthError(res)) return;
            if (!res.ok) throw new Error('Delete failed');
            const data = await res.json();

            if (itemToDelete) deletedItemRef.current = { ...itemToDelete };
            if (data.dashboard) setDashboard(data.dashboard);
            else fetchDashboard();

            toast(
                (t) => (
                    <span className="flex items-center gap-3 text-sm">
                        <span>Item deleted.</span>
                        <button
                            className="rounded-md bg-stone-900 px-3 py-1 text-xs font-semibold text-white"
                            onClick={() => {
                                toast.dismiss(t.id);
                                handleUndoDelete();
                            }}
                        >
                            Undo
                        </button>
                    </span>
                ),
                { duration: 5000 }
            );

            setTimeout(() => { deletedItemRef.current = null; }, 5500);
        } catch {
            toast.error('Could not delete item.');
        }
    };

    const handleUndoDelete = async () => {
        const item = deletedItemRef.current;
        if (!item) return;
        try {
            const { id, ...body } = item; // eslint-disable-line no-unused-vars
            const res = await fetch(ENDPOINTS.WEDDING_BUDGET_ITEMS, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(body),
            });
            if (handleAuthError(res)) return;
            if (!res.ok) throw new Error('Undo failed');
            const data = await res.json();
            deletedItemRef.current = null;
            if (data.dashboard) setDashboard(data.dashboard);
            else fetchDashboard();
            toast.success('Item restored.');
        } catch {
            toast.error('Could not undo deletion.');
        }
    };

    const handleTogglePaid = async (item) => {
        try {
            const res = await fetch(`${ENDPOINTS.WEDDING_BUDGET_ITEMS}/${item.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                // Only the field being changed. Echoing the whole item back
                // would re-send every other value too, so a stale row in this
                // view could overwrite an edit made elsewhere -- and an item
                // whose stored amount is unreadable serializes its cost as
                // null, which the API now (correctly) refuses as a request to
                // clear a NOT NULL column.
                body: JSON.stringify({ is_paid: !item.is_paid }),
            });
            if (handleAuthError(res)) return;
            if (!res.ok) throw new Error('Update failed');
            const data = await res.json();
            toast.success(item.is_paid ? 'Marked as unpaid' : 'Marked as paid!');
            if (data.dashboard) setDashboard(data.dashboard);
            else fetchDashboard();
        } catch {
            toast.error('Could not update item.');
        }
    };

    // ── settings save ─────────────────────────────────────────────────────────

    const handleSettingsSaved = (data) => {
        setDashboard({
            ...(dashboard ?? {}),
            settings: data.settings,
            ...(data.dashboard || {}),
        });
        setShowSettingsModal(false);
    };

    // ── render ────────────────────────────────────────────────────────────────

    // Never loaded, first attempt still in flight.
    if (resource.phase === 'loading') {
        return (
            <div role="status" aria-live="polite" className="flex items-center justify-center py-20 text-stone-600">
                <Loader2 size={28} className="animate-spin mr-3" aria-hidden="true" />
                <span className="text-sm font-medium">Loading budget…</span>
            </div>
        );
    }

    // Never loaded and the request failed. Render nothing derived — no totals,
    // no target, no "No budget items yet".
    if (resource.failedFirstLoad) {
        return (
            <DataErrorPanel
                title="We could not load your wedding budget"
                error={resource.error}
                onRetry={resource.load}
                retrying={resource.busy}
            />
        );
    }

    const barPercent = percentEst == null ? 0 : Math.min(100, percentEst);

    return (
        <div className="space-y-6">

                {resource.isStale && (
                    <StaleDataNotice
                        label="budget"
                        error={resource.error}
                        onRetry={resource.load}
                        retrying={resource.busy}
                        loadedAt={resource.loadedAt}
                    />
                )}

                {/* Settings button row */}
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={() => setShowSettingsModal(true)}
                        className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-xl bg-stone-100 px-3 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-200 transition"
                        title="Budget Settings"
                        aria-label="Budget settings"
                    >
                        <Settings size={15} aria-hidden="true" />
                        <span className="hidden sm:inline">Set Budget</span>
                    </button>
                </div>

                {/* Budget overview card */}
                <div className="rounded-2xl border border-amber-200 bg-white/90 shadow-sm p-5 space-y-4">
                    {/* Progress bar */}
                    <div className="space-y-2">
                        <div className="flex items-end justify-between gap-2">
                            <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Estimated vs Target</span>
                            <span className={clsx('text-xs font-bold', percentEst == null ? 'text-red-600' : 'text-stone-700')}>
                                {percentEst == null
                                    ? (conversionAvailable ? 'No target set' : 'Unavailable')
                                    : `${barPercent.toFixed(1)}%`}
                            </span>
                        </div>
                        <div className="h-3 rounded-full bg-stone-100 overflow-hidden">
                            <div
                                className={clsx('h-full rounded-full transition-all duration-500', progressColor(percentEst ?? 0))}
                                style={{ width: `${barPercent}%` }}
                            />
                        </div>
                        <div className="flex items-center justify-between text-xs text-stone-500">
                            <span className="font-semibold text-stone-700 text-sm">{fmt(estimated, primaryCurrency)}</span>
                            <span>
                                / {hasTarget
                                    ? fmt(budgetTarget, primaryCurrency)
                                    : <span className="italic text-stone-500">no target set</span>}
                            </span>
                        </div>
                    </div>

                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-2xl border border-stone-100 bg-stone-50 p-3 text-center">
                            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Estimated</div>
                            <div className="text-sm font-bold text-stone-800">{fmt(estimated, primaryCurrency)}</div>
                        </div>
                        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-3 text-center">
                            <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-1">Actual</div>
                            <div className="text-sm font-bold text-stone-800">{fmt(actual, primaryCurrency)}</div>
                        </div>
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3 text-center">
                            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">Paid</div>
                            <div className="text-sm font-bold text-stone-800">{fmt(paid, primaryCurrency)}</div>
                        </div>
                    </div>

                    {/* FX note / unavailable state */}
                    {dashboard?.fx_error ? (
                        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-medium text-red-700">
                            {dashboard.fx_error}
                        </p>
                    ) : dashboard?.fx_note ? (
                        <p className="text-[11px] text-stone-500 italic">{dashboard.fx_note}</p>
                    ) : null}
                </div>

                {/* Add item button */}
                <div>
                    <button
                        type="button"
                        onClick={openAdd}
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 transition shadow-sm"
                    >
                        <Plus size={15} aria-hidden="true" /> Add Item
                    </button>
                </div>

                {/* Category breakdown */}
                {categories.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-stone-300 bg-white/90 py-16 text-center text-stone-500">
                        <DollarSign size={36} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm font-medium">No budget items yet</p>
                        <p className="text-xs mt-1">Add your first item to get started.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {categories.map((cat, index) => (
                            <CategoryCard
                                key={cat.category}
                                cat={cat}
                                panelId={`budget-category-panel-${index}`}
                                budgetTarget={budgetTarget}
                                primaryCurrency={primaryCurrency}
                                expanded={expandedCategories.has(cat.category)}
                                onToggle={() => toggleCategory(cat.category)}
                                onEdit={openEdit}
                                onDelete={handleDelete}
                                onTogglePaid={handleTogglePaid}
                            />
                        ))}
                    </div>
                )}

            {/* Modals — portalled to <body>, see the note on backgroundRef. */}
            {showSettingsModal && createPortal(
                <SettingsModal
                    settings={dashboard?.settings}
                    onClose={() => setShowSettingsModal(false)}
                    onSaved={handleSettingsSaved}
                    backgroundRef={backgroundRef}
                />,
                document.body
            )}

            {showItemModal && createPortal(
                <ItemModal
                    item={editingItem}
                    categories={categories}
                    primaryCurrency={primaryCurrency}
                    onClose={closeItemModal}
                    onSaved={handleItemSaved}
                    backgroundRef={backgroundRef}
                />,
                document.body
            )}
        </div>
    );
}
